import { useEffect, useMemo, useState } from 'react';
import type { EChartsCoreOption } from 'echarts/core';
import { readError, request } from '../../app/api';
import { formatDateTime } from '../../shared/date-time';
import { AnalyticsFilterBar } from './AnalyticsFilterBar';
import {
  createAnalyticsFilterQuery,
  type AnalyticsFilterScope,
} from './AnalyticsFilterScope';
import { AnalyticsChart } from './AnalyticsChart';
import { CountryBadge, SessionCountry } from './GeoUi';
import { PlatformBadge } from './PlatformUi';
import { WorldMapChart, type GeographyMetricKey, type GeographyScale } from './WorldMapChart';
import type { GeographyMetrics, GeographyReport, SessionSummary } from './types';

interface OverviewReport {
  range: { from: string; to: string };
  installations: number;
  sessions: number;
  events: number;
  batches: number;
  averageSessionSeconds: number;
  skewedBatches: number;
  duplicateEvents: number;
  topEvents: Array<{ key: string; name: string; value: number }>;
  platforms: Array<{ platformCode: number; sessions: number; events: number }>;
}

interface OverviewScope {
  metric: GeographyMetricKey;
  scale: GeographyScale;
  countryCode: string | null;
}

const metricLabels: Record<GeographyMetricKey, string> = {
  sessions: 'Sessions',
  newInstalls: 'New installs',
  activeInstalls: 'Active installs',
  events: 'Events',
  batches: 'Batches',
  payloadBytes: 'Payload',
};

export function OverviewView(props: {
  environmentId: string;
  filters: AnalyticsFilterScope;
  onChangeFilters: (filters: AnalyticsFilterScope) => void;
  onOpenSession: (id: string) => void;
}): React.JSX.Element {
  const [scope, setScope] = useState<OverviewScope>(readInitialScope);
  const [overview, setOverview] = useState<OverviewReport | null>(null);
  const [geography, setGeography] = useState<GeographyReport | null>(null);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    const query = createAnalyticsFilterQuery(props.environmentId, props.filters);
    const geographyQuery = new URLSearchParams(query);
    if (scope.countryCode !== null) geographyQuery.set('countryCode', scope.countryCode);
    Promise.all([
      request<OverviewReport>(`/admin/v1/reports/overview?${query.toString()}`),
      request<GeographyReport>(`/admin/v1/reports/geography?${geographyQuery.toString()}`),
      request<{ sessions: SessionSummary[] }>(`/admin/v1/reports/sessions?${query.toString()}&page=1&pageSize=8`),
    ]).then((responses) => {
      if (!active) return;
      setOverview(responses[0]);
      setGeography(responses[1]);
      setSessions(responses[2].sessions);
      setError('');
    }).catch((caught: unknown) => {
      if (active) setError(readError(caught));
    }).finally(() => {
      if (active) setLoading(false);
    });
    writeOverviewScopeToLocation(scope);
    return () => { active = false; };
  }, [props.environmentId, props.filters, scope]);

  const trendOption = useMemo(() => createTrendOption(geography, scope.metric), [geography, scope.metric]);
  if (overview === null || geography === null) {
    return <section className="analytics-page overview-workspace"><AnalyticsFilterBar environmentId={props.environmentId} scope={props.filters} onChange={changeFilters} />{error !== '' && <div className="error-banner">{error}</div>}<div className="surface empty">{loading ? 'Loading analytics geography…' : 'Waiting for the first analytics data.'}</div></section>;
  }

  const selected = geography.selectedCountry;
  const maximumEventCount = Math.max(1, ...overview.topEvents.map(readCount));
  const rankedCountries = [...geography.countries].sort((left, right) => readMetric(right, scope.metric) - readMetric(left, scope.metric)).slice(0, 15);
  const totalKnown = geography.countries.reduce((sum, country) => sum + readMetric(country, scope.metric), 0);
  const unknownValue = readMetric(geography.unknown, scope.metric);
  return (
    <section className="analytics-page overview-workspace">
      <AnalyticsFilterBar environmentId={props.environmentId} scope={props.filters} onChange={changeFilters} />
      {error !== '' && <div className="error-banner">{error}</div>}
      <section className="metric-grid">
        <Metric label="Active installs" value={formatNumber(overview.installations)} detail="sent events in this scope" />
        <Metric label="Sessions" value={formatNumber(overview.sessions)} detail={`${formatDuration(overview.averageSessionSeconds * 1000)} average`} />
        <Metric label="Events" value={formatNumber(overview.events)} detail="accepted facts" />
        <Metric label="Batches" value={formatNumber(overview.batches)} detail="accepted transports" accent />
      </section>
      <section className="geography-grid">
        <article className="surface geography-map-surface">
          <div className="surface-heading geography-map-heading">
            <div><span className="eyebrow">Geography</span><h2>Activity around the world</h2><small>Country is resolved from each received batch, not from the device.</small></div>
            <div className="map-controls">
              <label>Metric<select value={scope.metric} onChange={changeMetric}>{renderMetricOptions()}</select></label>
              <label>Scale<select value={scope.scale} onChange={changeScale}><option value="linear">Linear</option><option value="logarithmic">Logarithmic</option></select></label>
              <button className="secondary-button clear-country-button" type="button" onClick={clearCountry} disabled={scope.countryCode === null}>Clear country</button>
            </div>
          </div>
          <WorldMapChart countries={geography.countries} metric={scope.metric} scale={scope.scale} selectedCountryCode={scope.countryCode} onSelectCountry={selectCountry} />
          <div className="map-footnote"><span>{formatNumber(totalKnown)} known · {formatNumber(unknownValue)} unknown</span><a href="https://db-ip.com" target="_blank" rel="noreferrer">IP geolocation by DB-IP</a></div>
        </article>
        <article className="surface country-ranking">
          <div className="surface-heading"><div><span className="eyebrow">Ranking</span><h2>{metricLabels[scope.metric]} by country</h2></div><strong>{geography.countries.length}</strong></div>
          <div className="country-rank-list">{rankedCountries.map(renderCountryRank)}</div>
          {rankedCountries.length === 0 && <Empty text="No country data in this scope yet." compact />}
          {unknownValue > 0 && <div className="unknown-country-row"><CountryBadge countryCode={null} /><strong>{formatMetric(unknownValue, scope.metric)}</strong></div>}
        </article>
      </section>
      {selected !== null && <section className="country-detail surface">
        <div className="surface-heading"><div><span className="eyebrow">Country drill-down</span><h2><CountryBadge countryCode={selected.countryCode} /></h2></div><button className="secondary-button" onClick={clearCountry}>Clear country</button></div>
        <section className="country-detail-metrics">{renderCountryMetric('Sessions', selected.metrics.sessions)}{renderCountryMetric('New installs', selected.metrics.newInstalls)}{renderCountryMetric('Active installs', selected.metrics.activeInstalls)}{renderCountryMetric('Events', selected.metrics.events)}{renderCountryMetric('Batches', selected.metrics.batches)}{renderCountryMetric('Payload', selected.metrics.payloadBytes, true)}</section>
        <section className="country-detail-grid">
          <article><div className="surface-heading"><div><span className="eyebrow">Daily activity</span><h3>{metricLabels[scope.metric]}</h3></div></div><AnalyticsChart option={trendOption} height={260} /></article>
          <article><div className="surface-heading"><div><span className="eyebrow">Sources</span><h3>Platforms</h3></div></div><div className="country-platform-list">{selected.platforms.map(renderCountryPlatform)}</div></article>
          <article><div className="surface-heading"><div><span className="eyebrow">Behavior</span><h3>Top events</h3></div></div><div className="rank-list">{selected.topEvents.map(renderTopEvent)}</div></article>
        </section>
        <section className="country-recent-grid">
          <article><div className="surface-heading"><div><span className="eyebrow">Recent</span><h3>Sessions</h3></div></div>{selected.recentSessions.map(renderCountrySession)}</article>
          <article><div className="surface-heading"><div><span className="eyebrow">First seen</span><h3>New installs</h3></div></div>{selected.recentInstallations.map(renderCountryInstall)}</article>
        </section>
      </section>}
      <section className="dashboard-grid">
        <article className="surface chart-surface">
          <div className="surface-heading"><div><span className="eyebrow">Activity</span><h2>Most frequent events</h2></div><small>current scope</small></div>
          <div className="bar-list">{overview.topEvents.map(renderOverviewEvent)}</div>
          {overview.topEvents.length === 0 && <Empty text="Tracked activity will appear here." compact />}
        </article>
        <article className="surface quality-surface"><span className="eyebrow">Data quality</span><h2>Transport health</h2><Quality label="Clock-skewed batches" value={overview.skewedBatches} /><Quality label="Duplicate events" value={overview.duplicateEvents} /><p>Unknown geography includes historical data, private addresses, and unresolved IPs.</p></article>
      </section>
      <RecentSessions sessions={sessions} onOpen={props.onOpenSession} />
    </section>
  );

  function changeMetric(event: React.ChangeEvent<HTMLSelectElement>): void { setScope({ ...scope, metric: event.target.value as GeographyMetricKey }); }
  function changeScale(event: React.ChangeEvent<HTMLSelectElement>): void { setScope({ ...scope, scale: event.target.value as GeographyScale }); }
  function changeFilters(filters: AnalyticsFilterScope): void {
    if (scope.countryCode !== null && filters.countries.length > 0 && !filters.countries.includes(scope.countryCode)) {
      setScope({ ...scope, countryCode: null });
    }
    props.onChangeFilters(filters);
  }
  function selectCountry(countryCode: string): void { setScope({ ...scope, countryCode }); }
  function clearCountry(): void { setScope({ ...scope, countryCode: null }); }

  function renderCountryRank(country: GeographyReport['countries'][number]): React.JSX.Element {
    const value = readMetric(country, scope.metric);
    return <button key={country.countryCode} className={country.countryCode === scope.countryCode ? 'active' : ''} onClick={() => selectCountry(country.countryCode)}><CountryBadge countryCode={country.countryCode} /><span>{country.continentCode ?? '—'}</span><strong>{formatMetric(value, scope.metric)}</strong></button>;
  }

  function renderCountryPlatform(platform: NonNullable<GeographyReport['selectedCountry']>['platforms'][number]): React.JSX.Element {
    return <div key={platform.platformCode}><PlatformBadge code={platform.platformCode} /><strong>{formatMetric(readMetric(platform, scope.metric), scope.metric)}</strong></div>;
  }

  function renderCountrySession(session: SessionSummary): React.JSX.Element {
    return <button className="country-recent-row" key={session.sessionId} onClick={() => props.onOpenSession(session.sessionId)}><span><strong>{shortId(session.sessionId)}</strong><small>{formatDate(session.startedAt)}</small></span><span><PlatformBadge code={session.platformCode} /><strong>{formatNumber(session.eventCount)} events</strong></span></button>;
  }

  function renderCountryInstall(installation: NonNullable<GeographyReport['selectedCountry']>['recentInstallations'][number]): React.JSX.Element {
    return <div className="country-recent-row" key={installation.installationId}><span><strong>{shortId(installation.installationId)}</strong><small>{formatDate(installation.firstSeenAt)}</small></span><span><PlatformBadge code={installation.firstPlatformCode} /><strong>{formatNumber(installation.sessionCount)} sessions</strong></span></div>;
  }

  function renderOverviewEvent(item: OverviewReport['topEvents'][number]): React.JSX.Element {
    return <div className="bar-row" key={item.key}><span>{item.name}</span><div><i style={{ width: `${(item.value / maximumEventCount) * 100}%` }} /></div><strong>{formatNumber(item.value)}</strong></div>;
  }
}

function RecentSessions(props: { sessions: SessionSummary[]; onOpen: (id: string) => void }): React.JSX.Element {
  return <article className="surface recent-sessions"><div className="surface-heading"><div><span className="eyebrow">Live trail</span><h2>Recent sessions</h2></div></div><div className="session-table geography-session-table"><div className="table-head"><span>Session</span><span>Started</span><span>Country</span><span>Platform</span><span>Duration</span><span>Events</span></div>{props.sessions.map((session) => <button key={session.sessionId} onClick={() => props.onOpen(session.sessionId)}><span><strong>{shortId(session.sessionId)}</strong><small>install {shortId(session.installationId)}</small></span><span>{formatDate(session.startedAt)}</span><span><SessionCountry countryCode={session.countryCode} countryCodes={session.countryCodes} /></span><span><PlatformBadge code={session.platformCode} /></span><span>{formatDuration(session.durationMs)}</span><span>{session.eventCount}</span></button>)}</div></article>;
}

function Metric(props: { label: string; value: string; detail: string; accent?: boolean }): React.JSX.Element {
  return <article className={props.accent === true ? 'metric accent' : 'metric'}><span>{props.label}</span><strong>{props.value}</strong><small>{props.detail}</small></article>;
}

function Quality(props: { label: string; value: number }): React.JSX.Element {
  return <div className="quality-row"><span>{props.label}</span><strong className={props.value === 0 ? 'good' : ''}>{props.value}</strong></div>;
}

function Empty(props: { text: string; compact?: boolean }): React.JSX.Element {
  return <div className={props.compact === true ? 'empty compact' : 'empty'}>{props.text}</div>;
}

function renderMetricOptions(): React.JSX.Element[] {
  return (Object.keys(metricLabels) as GeographyMetricKey[]).map((key) => <option key={key} value={key}>{metricLabels[key]}</option>);
}

function renderCountryMetric(label: string, value: number, bytes = false): React.JSX.Element {
  return <span key={label}><small>{label}</small><strong>{bytes ? formatBytes(value) : formatNumber(value)}</strong></span>;
}

function renderTopEvent(event: { key: string; name: string; value: number }): React.JSX.Element {
  return <div key={event.key}><span>{event.name}</span><strong>{formatNumber(event.value)}</strong></div>;
}

function createTrendOption(geography: GeographyReport | null, metric: GeographyMetricKey): EChartsCoreOption {
  const trend = geography?.selectedCountry?.trend ?? [];
  return {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis' },
    grid: { left: 44, right: 15, top: 16, bottom: 28 },
    xAxis: { type: 'category', data: trend.map(readTrendDay), axisLabel: { color: '#65748a' }, axisLine: { lineStyle: { color: '#28354a' } } },
    yAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#65748a' }, splitLine: { lineStyle: { color: '#202b3a' } } },
    series: [{ type: 'line', name: metricLabels[metric], data: trend.map((entry) => readMetric(entry, metric)), showSymbol: trend.length < 32, smooth: true, lineStyle: { width: 2, color: '#86a8ff' }, itemStyle: { color: '#86a8ff' }, areaStyle: { color: 'rgba(92,131,233,.13)' } }],
  };
}

function readInitialScope(): OverviewScope {
  const query = new URLSearchParams(window.location.search);
  const metric = parseMetric(query.get('overviewMetric'));
  const scale = query.get('overviewScale') === 'logarithmic' ? 'logarithmic' : 'linear';
  const country = query.get('overviewCountry');
  return {
    metric,
    scale,
    countryCode: country !== null && /^[A-Z]{2}$/.test(country) ? country : null,
  };
}

function writeOverviewScopeToLocation(scope: OverviewScope): void {
  const url = new URL(window.location.href);
  url.searchParams.set('overviewMetric', scope.metric);
  url.searchParams.set('overviewScale', scope.scale);
  setOptionalParameter(url.searchParams, 'overviewCountry', scope.countryCode);
  window.history.replaceState({}, '', url);
}

function parseMetric(value: string | null): GeographyMetricKey { return value !== null && value in metricLabels ? value as GeographyMetricKey : 'sessions'; }
function setOptionalParameter(query: URLSearchParams, key: string, value: string | null): void { if (value === null) query.delete(key); else query.set(key, value); }
function readMetric(metrics: GeographyMetrics, key: GeographyMetricKey): number { return metrics[key]; }
function readTrendDay(entry: { day: string }): string { return entry.day.slice(5); }
function readCount(value: { value: number }): number { return value.value; }
function formatMetric(value: number, metric: GeographyMetricKey): string { return metric === 'payloadBytes' ? formatBytes(value) : formatNumber(value); }
function formatBytes(value: number): string { if (value < 1024) return `${value} B`; if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`; return `${(value / (1024 * 1024)).toFixed(1)} MB`; }
function formatNumber(value: number): string { return new Intl.NumberFormat().format(value); }
function formatDuration(milliseconds: number): string { const seconds = Math.round(milliseconds / 1000); return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`; }
function formatDate(value: string): string { return formatDateTime(value); }
function shortId(value: string): string { return value.slice(0, 8); }
